/* Size guide — the size finder calculators.
 *
 * One controller serves every garment; the section tells it which to run via
 * `data-size-guide-finder="joggers|hoodie|kids"`. Every band it reads comes from
 * the JSON the section prints from `snippets/size-guide-data.liquid`, so the
 * finder and the reference charts can never disagree.
 *
 * Joggers: waist (typed or "usual size") + optional hip + height -> size + length.
 * Hoodie:  height -> size.
 * Kids:    waist -> size, height -> leg length. Read independently, so either
 *          measurement on its own already answers half the question.
 */
(function () {
  'use strict';

  /* Several size guide sections can appear on one page and each links this
     file, so the browser executes it once per tag. Bind only the first time. */
  if (window.sizeGuideFinderLoaded) return;
  window.sizeGuideFinderLoaded = true;


  var UNIT_ATTRIBUTE = 'data-size-guide-unit-preference';
  var AUDIENCE_ATTRIBUTE = 'data-size-guide-audience';

  /* A woman's usual UK size maps straight onto our own. A man's usual trouser
     waist maps onto a letter size; 38" has no size and routes to contact. */
  var MENS_USUAL_TO_SIZE = { '28': 'XS', '30': 'S', '32': 'M', '34': 'L', '36': 'XL' };

  /* Where a typed measurement stops reading as inches and starts reading as
     centimetres. It has to fall between the two plausible ranges for the
     garment: adult waists run ~24-44" / 61-112 cm, so 60 splits them cleanly,
     but a child's run ~20-26" / 51-66 cm and straddle 60. */
  var UNIT_CROSSOVER = { joggers: 60, hoodie: 60, kids: 40 };

  function bandFor(bands, value) {
    for (var i = 0; i < bands.length; i++) {
      if (value >= bands[i].loIn && value < bands[i].hiIn) return bands[i];
    }
    return bands[bands.length - 1];
  }

  function labelForHeight(bands, centimetres) {
    for (var i = 0; i < bands.length; i++) {
      if (centimetres <= bands[i].hiCm) return bands[i].label;
    }
    return bands[bands.length - 1].label;
  }

  /* Like labelForHeight, but returns null past the tallest band instead of
     clamping to it — kids sizing genuinely runs out, and saying so is more
     useful than recommending the largest child size to an adult-height teen. */
  function labelWithinHeight(bands, centimetres) {
    for (var i = 0; i < bands.length; i++) {
      if (centimetres <= bands[i].hiCm) return bands[i].label;
    }
    return null;
  }

  function largerSize(order, a, b) {
    return order.indexOf(a) >= order.indexOf(b) ? a : b;
  }

  /* Reveal exactly one of a figure's alternatives and hide the others. A kids
     figure holds a number, a "still need this measurement" nudge and a note. */
  function showOne(alternatives, chosen) {
    for (var i = 0; i < alternatives.length; i++) {
      if (alternatives[i]) alternatives[i].hidden = alternatives[i] !== chosen;
    }
  }

  function fill(template, values) {
    var text = template;
    for (var token in values) {
      if (Object.prototype.hasOwnProperty.call(values, token)) {
        text = text.split('[' + token + ']').join(values[token]);
      }
    }
    return text;
  }

  function toNumber(value) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : NaN;
  }

  function Finder(root) {
    this.root = root;
    this.garment = root.getAttribute('data-size-guide-finder');

    var payload = root.querySelector('[data-size-guide-bands]');
    this.bands = payload ? JSON.parse(payload.textContent) : null;
    if (!this.bands) return;

    this.unit = 'in';
    this.waistMode = 'measure';

    this.inputs = root.querySelectorAll('[data-size-guide-input]');

    this.parts = {
      empty: root.querySelector('[data-size-guide-result="empty"]'),
      body: root.querySelector('[data-size-guide-result="body"]'),
      contact: root.querySelector('[data-size-guide-result="contact"]'),
      size: root.querySelector('[data-size-guide-result="size"]'),
      length: root.querySelector('[data-size-guide-result="length"]'),
      lengthGroup: root.querySelector('[data-size-guide-result="length-group"]'),
      explanation: root.querySelector('[data-size-guide-result="explanation"]'),
      emptyText: root.querySelector('[data-size-guide-result="empty-text"]'),

      /* Kids only: either half of the answer can still be missing, so each
         figure has a nudge to show in place of a number. */
      sizePending: root.querySelector('[data-size-guide-result="size-pending"]'),
      lengthPending: root.querySelector('[data-size-guide-result="length-pending"]'),
      lengthNote: root.querySelector('[data-size-guide-result="length-note"]')
    };

    this.messages = {
      prompt: root.getAttribute('data-prompt') || '',
      unitMismatch: root.getAttribute('data-unit-mismatch') || '',
      explanation: root.getAttribute('data-explanation') || '',
      hoodieExplanation: root.getAttribute('data-hoodie-explanation') || '',
      kidsExplanation: root.getAttribute('data-kids-explanation') || '',
      kidsAdultExplanation: root.getAttribute('data-kids-adult-explanation') || '',
      kidsSizeOnly: root.getAttribute('data-kids-size-only') || '',
      kidsLengthOnly: root.getAttribute('data-kids-length-only') || ''
    };

    this.bind();
    this.update();
  }

  /* Resolve an input for the audience showing now. A field inside an audience
     block only counts for that audience; an untagged field (height) always counts. */
  Finder.prototype.field = function (name) {
    var audience = this.audience();
    var matches = this.root.querySelectorAll('[data-size-guide-input="' + name + '"]');
    for (var i = 0; i < matches.length; i++) {
      var scope = matches[i].closest('[data-size-guide-audience]');
      if (!scope || scope.getAttribute('data-size-guide-audience') === audience) return matches[i];
    }
    return matches[0] || null;
  };

  Finder.prototype.clearWaistFields = function () {
    this.root.querySelectorAll('[data-size-guide-input="waist"], [data-size-guide-input="usual"]')
      .forEach(function (input) { input.value = ''; });
  };

  Finder.prototype.bind = function () {
    var self = this;

    this.inputs.forEach(function (input) {
      input.addEventListener('input', function () { self.update(); });
      input.addEventListener('change', function () { self.update(); });
    });

    this.root.querySelectorAll('[data-size-guide-waist-mode]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.waistMode = button.getAttribute('data-size-guide-waist-mode');
        self.clearWaistFields();
        self.renderWaistMode();
        self.update();
      });
    });

    document.addEventListener('size-guide:unitchange', function (event) {
      self.unit = event.detail.unit;
      self.renderUnits();
      self.update();
    });

    // The audience switch lives in another section; re-run when it changes.
    if (this.garment === 'joggers') {
      new MutationObserver(function () {
        self.applyAudienceDefaults();
        self.update();
      }).observe(document.documentElement, { attributes: true, attributeFilter: [AUDIENCE_ATTRIBUTE] });
      this.applyAudienceDefaults();
    }

    this.renderUnits();
    this.renderWaistMode();
  };

  Finder.prototype.audience = function () {
    return document.documentElement.getAttribute(AUDIENCE_ATTRIBUTE) === 'men' ? 'men' : 'women';
  };

  /* Men default to the "usual trouser waist" dropdown, women to a measurement. */
  Finder.prototype.applyAudienceDefaults = function () {
    this.waistMode = this.audience() === 'men' ? 'usual' : 'measure';
    this.clearWaistFields();
    this.renderWaistMode();
  };

  Finder.prototype.renderWaistMode = function () {
    var mode = this.waistMode;
    this.root.querySelectorAll('[data-size-guide-waist-view]').forEach(function (view) {
      view.hidden = view.getAttribute('data-size-guide-waist-view') !== mode;
    });
  };

  Finder.prototype.renderUnits = function () {
    var unit = this.unit;
    this.root.querySelectorAll('[data-size-guide-unit-view]').forEach(function (view) {
      view.hidden = view.getAttribute('data-size-guide-unit-view') !== unit;
    });
  };

  Finder.prototype.toInches = function (value) {
    var number = toNumber(value);
    if (!isFinite(number) || number <= 0) return NaN;
    return this.unit === 'cm' ? number / 2.54 : number;
  };

  Finder.prototype.heightInCentimetres = function () {
    var centimetresField = this.field('height-cm');
    if (this.unit === 'cm') {
      var centimetres = toNumber(centimetresField && centimetresField.value);
      return centimetres > 0 ? centimetres : NaN;
    }
    var feetField = this.field('height-feet');
    var inchesField = this.field('height-inches');
    var feet = toNumber(feetField && feetField.value);
    var inches = toNumber(inchesField && inchesField.value) || 0;
    if (!isFinite(feet) || feet <= 0) return NaN;
    return (feet * 12 + inches) * 2.54;
  };

  /* A value far outside the plausible range for the selected unit is almost
     always the other unit typed by mistake — say so instead of guessing. */
  Finder.prototype.looksLikeWrongUnit = function (field) {
    if (!field || field.value === '') return false;
    var value = toNumber(field.value);
    if (!isFinite(value)) return false;
    var crossover = UNIT_CROSSOVER[this.garment] || 60;
    return this.unit === 'in' ? value > crossover : value < crossover;
  };

  Finder.prototype.resolveWaistSize = function (audience) {
    if (this.waistMode === 'usual') {
      var usualField = this.field('usual');
      var usual = usualField ? usualField.value : '';
      if (!usual) return null;
      return audience === 'men' ? MENS_USUAL_TO_SIZE[usual] || null : usual;
    }
    var waistField = this.field('waist');
    var inches = this.toInches(waistField ? waistField.value : '');
    if (!isFinite(inches)) return null;
    var bands = audience === 'men' ? this.bands.mensWaist : this.bands.womensWaist;
    return bandFor(bands, inches).size;
  };

  Finder.prototype.update = function () {
    if (this.garment === 'hoodie') return this.updateHoodie();
    if (this.garment === 'kids') return this.updateKids();
    return this.updateJoggers();
  };

  Finder.prototype.show = function (state, message) {
    this.parts.empty.hidden = state !== 'empty';
    this.parts.body.hidden = state !== 'result';
    if (this.parts.contact) this.parts.contact.hidden = state !== 'contact';
    if (state === 'empty' && this.parts.emptyText) {
      this.parts.emptyText.textContent = message || this.messages.prompt;
    }
  };

  Finder.prototype.updateHoodie = function () {
    var centimetres = this.heightInCentimetres();
    if (!isFinite(centimetres)) return this.show('empty');

    // Bands are defined in inches; convert so a height in cm resolves identically.
    var inches = centimetres / 2.54;
    var bands = this.bands.heightBands;
    var size = bands[bands.length - 1].size;
    for (var i = 0; i < bands.length; i++) {
      if (inches <= bands[i].hiIn) { size = bands[i].size; break; }
    }

    this.parts.size.textContent = size;
    if (this.parts.lengthGroup) this.parts.lengthGroup.hidden = true;
    if (this.parts.explanation) {
      this.parts.explanation.textContent = fill(this.messages.hoodieExplanation, { size: size });
    }
    this.show('result');
  };

  /* Kids: waist and height answer different halves of the question, so either
     one alone is already worth showing. A child past the tallest length band has
     outgrown kids sizing — say so rather than recommending the largest age. */
  Finder.prototype.updateKids = function () {
    var waistField = this.field('waist');
    if (this.looksLikeWrongUnit(waistField)) return this.show('empty', this.messages.unitMismatch);

    var waistInches = this.toInches(waistField ? waistField.value : '');
    var size = isFinite(waistInches) ? bandFor(this.bands.kidsWaist, waistInches).size : null;

    var centimetres = this.heightInCentimetres();
    var hasHeight = isFinite(centimetres);
    var length = hasHeight ? labelWithinHeight(this.bands.kidsLength, centimetres) : null;
    var isAdultHeight = hasHeight && length === null;

    if (!size && !hasHeight) return this.show('empty');

    this.parts.size.textContent = size || '';
    showOne(
      [this.parts.size, this.parts.sizePending],
      size ? this.parts.size : this.parts.sizePending
    );

    this.parts.length.textContent = length || '';
    showOne(
      [this.parts.length, this.parts.lengthNote, this.parts.lengthPending],
      isAdultHeight ? this.parts.lengthNote : length ? this.parts.length : this.parts.lengthPending
    );

    if (this.parts.lengthGroup) this.parts.lengthGroup.hidden = false;

    if (this.parts.explanation) {
      var explanation = '';
      if (size && length) {
        explanation = fill(this.messages.kidsExplanation, { size: size, length: length });
      } else if (size && isAdultHeight) {
        explanation = this.messages.kidsAdultExplanation;
      } else if (size) {
        explanation = this.messages.kidsSizeOnly;
      } else if (length) {
        explanation = this.messages.kidsLengthOnly;
      }
      this.parts.explanation.textContent = explanation;
      this.parts.explanation.hidden = explanation === '';
    }

    this.show('result');
  };

  Finder.prototype.updateJoggers = function () {
    var audience = this.audience();

    var mismatched =
      (this.waistMode === 'measure' && this.looksLikeWrongUnit(this.field('waist'))) ||
      (audience === 'women' && this.looksLikeWrongUnit(this.field('hip')));

    if (mismatched) return this.show('empty', this.messages.unitMismatch);

    var centimetres = this.heightInCentimetres();
    if (!isFinite(centimetres)) return this.show('empty');

    // Top of the men's range has no size — route to contact instead.
    var usualField = this.field('usual');
    if (audience === 'men' && this.waistMode === 'usual' && usualField && usualField.value === '38') {
      return this.show('contact');
    }

    var size = this.resolveWaistSize(audience);
    if (!size) return this.show('empty');

    if (audience === 'women') {
      // Curvy exception: a hip that suggests a bigger size wins, because the
      // drawstring can take in a loose waist but a tight seat cannot be let out.
      var hipField = this.field('hip');
      var hipInches = this.toInches(hipField ? hipField.value : '');
      if (isFinite(hipInches) && hipInches > 0) {
        var hipSize = bandFor(this.bands.womensHip, hipInches).size;
        size = largerSize(this.bands.womensSizeOrder, size, hipSize);
      }
    }

    var heightBands = audience === 'men' ? this.bands.mensHeight : this.bands.womensHeight;

    this.parts.size.textContent = size;
    this.parts.length.textContent = labelForHeight(heightBands, centimetres);
    if (this.parts.lengthGroup) this.parts.lengthGroup.hidden = false;
    if (this.parts.explanation) this.parts.explanation.textContent = this.messages.explanation;
    this.show('result');
  };

  function init(root) {
    (root || document).querySelectorAll('[data-size-guide-finder]').forEach(function (element) {
      new Finder(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
